import React, { useState, useCallback } from 'react';
import {
  Upload, X, CheckCircle2, FileAudio, Loader2,
  HardDrive, Music
} from 'lucide-react';
import './SwapTrackModal.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SwapTrackModal = ({ track, position, crateId, onClose, onSwapped }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);

  const getAuthToken = useCallback(() => localStorage.getItem('beatseek_token'), []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['audio/mpeg', 'audio/wav', 'audio/flac', 'audio/aiff', 'audio/mp3'];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|flac|aiff|m4a)$/i)) {
        setError('Please select a valid audio file (MP3, WAV, FLAC, AIFF)');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleSwap = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const token = getAuthToken();
      
      // Generate a unique track ID for the local file
      const newTrackId = `local_${Date.now()}_${selectedFile.name.replace(/[^a-z0-9]/gi, '_')}`;
      
      // Call the swap endpoint
      const response = await fetch(
        `${BACKEND_URL}/api/crates/${crateId}/tracks/${position}/swap-verified`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            new_track_id: newTrackId,
            source: 'local_upload',
            file_path: selectedFile.name,
            external_url: null
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to swap track');
      }

      const data = await response.json();
      
      // Call success callback
      if (onSwapped) {
        onSwapped(data.track, data.stats);
      }
      
      onClose();
    } catch (err) {
      console.error('Error swapping track:', err);
      setError(err.message || 'Failed to swap track');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="swap-modal-overlay" onClick={onClose}>
      <div className="swap-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="swap-modal-header">
          <h3>Swap for Verified Track</h3>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Current Track Info */}
        <div className="current-track-info">
          <div className="track-artwork">
            {track.artwork_url ? (
              <img src={track.artwork_url} alt="" />
            ) : (
              <div className="no-artwork"><Music size={24} /></div>
            )}
          </div>
          <div className="track-details">
            <div className="track-title">{track.title}</div>
            <div className="track-artist">{track.artist}</div>
            <div className="track-status mock">
              Mock Track - {track.source}
            </div>
          </div>
        </div>

        {/* Upload Section */}
        <div className="upload-section">
          <p className="upload-description">
            Upload the verified version of this track from your local library. 
            All track metadata (BPM, key, position, notes) will be preserved.
          </p>

          <div className="upload-area">
            <input
              type="file"
              id="swap-file-input"
              accept=".mp3,.wav,.flac,.aiff,.m4a,audio/*"
              onChange={handleFileSelect}
              className="file-input"
            />
            <label htmlFor="swap-file-input" className="upload-label">
              {selectedFile ? (
                <>
                  <FileAudio size={32} className="file-icon" />
                  <span className="file-name">{selectedFile.name}</span>
                  <span className="file-size">
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </span>
                </>
              ) : (
                <>
                  <Upload size={32} />
                  <span>Click to select audio file</span>
                  <span className="file-types">MP3, WAV, FLAC, AIFF</span>
                </>
              )}
            </label>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>

        {/* Result Preview */}
        {selectedFile && (
          <div className="swap-preview">
            <div className="preview-row">
              <span className="label">After swap:</span>
              <div className="preview-badge verified">
                <CheckCircle2 size={14} />
                Verified (Local Upload)
              </div>
            </div>
            <div className="preview-note">
              <HardDrive size={14} />
              Track will be exportable to DJ software
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="swap-modal-actions">
          <button className="cancel-btn" onClick={onClose} data-testid="swap-cancel-btn">
            Cancel
          </button>
          <button 
            className="swap-btn"
            onClick={handleSwap}
            disabled={!selectedFile || isUploading}
            data-testid="swap-confirm-btn"
          >
            {isUploading ? (
              <>
                <Loader2 size={18} className="spinning" />
                Swapping...
              </>
            ) : (
              <>
                <CheckCircle2 size={18} />
                Swap to Verified
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SwapTrackModal;
